import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Hooks
import { useUserManagement } from "../../hooks/useUserManagement";
import { useUserOperations } from "../../hooks/useUserOperations";
import { useDepartmentOperations } from "../../hooks/useDepartmentOperations";
import { useDivisionOperations } from "../../hooks/useDivisionOperations";

// Components
import UserModal from "../../components/UserManagement/UserModal";
import DepartmentModal from "../../components/UserManagement/DepartmentModal";
import DivisionModal from "../../components/UserManagement/DivisionModal";
import UserTable from "../../components/UserManagement/UserTable";
import DepartmentTable from "../../components/UserManagement/DepartmentTable";
import DivisionTable from "../../components/UserManagement/DivisionTable";

// Utils
import {
  TABS,
  ROWS_PER_PAGE,
  filterData,
  getTotalPages,
  getDisplayedData,
  sortData,
} from "../../utils/userManagementUtils";

// Styles
import "./../managerPage/css/table.css";
import "./../managerPage/css/TabSwitcher.css";
import "./../managerPage/css/dropdown.css";
import "./../managerPage/css/form.css";

const CfmUser = () => {
  const navigate = useNavigate();
  // ========== Main Hook ==========
  const userManagement = useUserManagement();
  const {
    // State
    searchTerm,
    setSearchTerm,
    activeTab,
    setActiveTab,
    currentPage,
    setCurrentPage,
    sortOrder,
    setSortOrder,
    showModal,
    setShowModal,
    showModalDepartment,
    setShowModalDepartment,
    showModalDivision,
    setShowModalDivision,
    showEditDepartmentModal,
    setShowEditDepartmentModal,
    userForm,
    setUserForm,
    selectedUserDivision,
    setSelectedUserDivision,
    emailError,
    setEmailError,
    departmentForm,
    setDepartmentForm,
    editDepartmentData,
    setEditDepartmentData,
    originalDepartmentData,
    setOriginalDepartmentData,
    selectedDepartmentId,
    setSelectedDepartmentId,
    divisionForm,
    setDivisionForm,
    isEditingDivision,
    setIsEditingDivision,
    editDivisionId,
    setEditDivisionId,
    formError,
    setFormError,
    departments,
    setDepartments,
    users,
    setUsers,
    roleIds,

    // API Data
    academies,
    isLoadingAcademies,
    departmentData,
    refetchDepartments,
    isLoadingDepartments,
    usersData,
    isLoadingUsers,
    refetchUsers,
    roles,
    isLoadingRoles,
    divisions,
    refetchDivisions,
    isLoadingDivisions,

    // Computed Values
    academyId,
    filteredDivisionsForUser,
    departmentOptions,
    divisionOptions,
    selectedDepartment,
    setSelectedDepartment,

    // Helper Functions
    getRoleIdForTab,

    // Mutations
    createUser,
    isCreatingUser,
    createDepartment,
    updateDepartment,
    isUpdatingDepartment,
    deleteDepartment,
    createDivision,
    updateDivision,
    isUpdatingDivision,
    deleteDivision,
    softDeleteUser,

    // Helper Functions
    showSuccessToast,
    showErrorAlert,
    showConfirmDialog,

    // Form Handlers
    resetUserForm,
    resetDepartmentForm,
    resetDivisionForm,
    handleUserFormChange,
    handleEmailChange,

    // Change Detection
    hasDepartmentChanges,
  } = userManagement;

  // ========== Operations Hooks ==========
  const userOperations = useUserOperations({
    userForm,
    selectedDepartment,
    selectedUserDivision,
    activeTab,
    academyId,
    getRoleIdForTab,
    createUser,
    showSuccessToast,
    showErrorAlert,
    resetUserForm,
    refetchUsers,
    setShowModal,
    usersData,
  });

  const departmentOperations = useDepartmentOperations({
    departmentForm,
    editDepartmentData,
    selectedDepartmentId,
    academyId,
    createDepartment,
    updateDepartment,
    deleteDepartment,
    refetchDepartments,
    showSuccessToast,
    showErrorAlert,
    setFormError,
    resetDepartmentForm,
    setShowModalDepartment,
    setShowEditDepartmentModal,
    setEditDepartmentData,
    setOriginalDepartmentData,
    setSelectedDepartmentId,
    departments,
  });

  const divisionOperations = useDivisionOperations({
    divisionForm,
    selectedDepartment,
    editDivisionId,
    createDivision,
    updateDivision,
    deleteDivision,
    refetchDivisions,
    showSuccessToast,
    showErrorAlert,
    setFormError,
    resetDivisionForm,
    setShowModalDivision,
    setIsEditingDivision,
    setEditDivisionId,
    setDivisionForm,
    setSelectedDepartment,
  });

  // ========== UI Handlers ==========
  const handleAddUserClick = useCallback(() => {
    setFormError(null);
    if (activeTab === "Department") {
      setShowModalDepartment(true);
    } else if (activeTab === "Division") {
      setShowModalDivision(true);
    } else {
      setShowModal(true);
    }
  }, [
    activeTab,
    setFormError,
    setShowModalDepartment,
    setShowModalDivision,
    setShowModal,
  ]);

  const handleSort = useCallback(
    (column) => {
      const newSortOrder =
        column === sortOrder.column ? !sortOrder.ascending : true;

      setSortOrder({
        column,
        ascending: newSortOrder,
      });

      const sortedData = sortData(users, column, newSortOrder);
      setUsers(sortedData);
    },
    [sortOrder, users, setSortOrder, setUsers]
  );

  // ========== Filtered and Paginated Data ==========
  const filteredData = useMemo(() => {
    if (activeTab === "Department") {
      return filterData(departments || [], searchTerm);
    }

    if (activeTab === "Division") {
      return filterData(divisions || [], searchTerm);
    }

    return (users || []).filter(
      (item) =>
        item.role?.name?.toLowerCase() === activeTab.toLowerCase() &&
        item.academyId === academyId &&
        Object.values(item).some((value) =>
          (value !== null && value !== undefined
            ? value.toString().toLowerCase()
            : ""
          ).includes(searchTerm.toLowerCase())
        )
    );
  }, [searchTerm, activeTab, departments, divisions, users, academyId]);

  const totalPages = getTotalPages(filteredData.length, ROWS_PER_PAGE);
  const displayedData = getDisplayedData(
    filteredData,
    currentPage,
    ROWS_PER_PAGE
  );

  // ========== Render Helper Components ==========
  const renderPagination = () => (
    <div className="pagination">
      <span>{filteredData.length} Results</span>
      <div>
        {[...Array(totalPages).keys()].slice(0, 5).map((num) => (
          <button
            key={num}
            className={currentPage === num + 1 ? "active" : ""}
            onClick={() => setCurrentPage(num + 1)}
          >
            {num + 1}
          </button>
        ))}
        {totalPages > 5 && (
          <>
            <span>...</span>
            <button onClick={() => setCurrentPage(totalPages)}>
              {totalPages}
            </button>
          </>
        )}
      </div>
    </div>
  );

  // ========== Render Helper Functions ==========
  const renderTabButtons = () => (
    <div className="border-b border-zinc-200 overflow-x-auto">
      <div className="flex space-x-4 sm:space-x-8 min-w-max">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-1 font-medium text-sm transition-colors ${activeTab === tab
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-zinc-600 hover:text-zinc-800"
              }`}
          >
            {tab}
          </button>
        ))}
      </div>
    </div>
  );

  const renderAddButton = () => (
    <div className="flex justify-end">
      <button
        onClick={handleAddUserClick}
        className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white text-sm font-medium rounded hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors shadow-sm"
      >
        <Plus size={16} />
        Add {activeTab}
      </button>
    </div>
  );

  const renderSearchBar = () => (
    <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
      <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto">
        {/* Search Input */}
        <div className="w-full lg:flex-1 lg:min-w-60">
          <div className="relative">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-2 pl-8 border border-zinc-300 focus:outline-none rounded text-sm"
            />
            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <Search className="w-4 h-4 text-gray-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ========== Main Render ==========
  return (
    <div className="bg-white p-4 sm:p-6 h-full space-y-6 sm:space-y-8">
      {/* Title and Description Section */}
      <div className="max-w-4xl">
        <h2 className="text-2xl font-bold text-zinc-800 mb-2">
          User Management
        </h2>
        <p className="text-zinc-600 mb-6">
          Manage and organize users across different roles and academies
        </p>
      </div>

      {/* Tabs and Add Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex-1 w-full">{renderTabButtons()}</div>
        <div className="sm:ml-4">{renderAddButton()}</div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {renderSearchBar()}

        {/* Data Table */}
        <div className="table-container">
          {activeTab === "Department" ? (
            <DepartmentTable
              displayedData={displayedData}
              handleEditDepartment={(departmentId) =>
                departmentOperations.handleEditDepartment(
                  departmentId,
                  departments,
                  setEditDepartmentData,
                  setOriginalDepartmentData,
                  setSelectedDepartmentId,
                  setShowEditDepartmentModal,
                  showErrorAlert
                )
              }
              handleDeleteDepartment={(departmentId) =>
                departmentOperations.handleDeleteDepartment(
                  departmentId,
                  showConfirmDialog,
                  deleteDepartment,
                  refetchDepartments,
                  showSuccessToast,
                  showErrorAlert,
                  departments
                )
              }
            />
          ) : activeTab === "Division" ? (
            <DivisionTable
              displayedData={displayedData}
              departments={departments}
              openEditDivisionModal={(division) =>
                divisionOperations.openEditDivisionModal(
                  division,
                  departments,
                  setIsEditingDivision,
                  setEditDivisionId,
                  setDivisionForm,
                  setSelectedDepartment,
                  setShowModalDivision
                )
              }
              handleDeleteDivision={(divisionId) =>
                divisionOperations.handleDeleteDivision(
                  divisionId,
                  showConfirmDialog,
                  deleteDivision,
                  refetchDivisions,
                  showSuccessToast,
                  showErrorAlert
                )
              }
            />
          ) : (
            <UserTable
              displayedData={displayedData}
              activeTab={activeTab}
              academies={academies}
              departments={departments}
              divisions={divisions}
              handleDeleteUser={(userId) => {
                // Simple delete function without user operations
                showConfirmDialog(
                  "Are you sure?",
                  "Do you want to delete this user?"
                ).then((result) => {
                  if (result.isConfirmed) {
                    softDeleteUser(userId)
                      .unwrap()
                      .then(() => {
                        showSuccessToast("User has been deleted.");
                        refetchUsers();
                      })
                      .catch(() => {
                        showErrorAlert("Failed to delete user");
                      });
                  }
                });
              }}
              onRowClick={(email) => navigate(`/cfm/users/${email}`)}
            />
          )}
        </div>

        {/* Pagination */}
        {renderPagination()}
      </div>

      {/* Modals */}
      <UserModal
        showModal={showModal}
        setShowModal={setShowModal}
        resetUserForm={resetUserForm}
        isEditingUser={false}
        activeTab={activeTab}
        userForm={userForm}
        handleUserFormChange={handleUserFormChange}
        handleEmailChange={handleEmailChange}
        emailError={emailError}
        selectedDepartment={selectedDepartment}
        setSelectedDepartment={setSelectedDepartment}
        selectedUserDivision={selectedUserDivision}
        setSelectedUserDivision={setSelectedUserDivision}
        departmentOptions={departmentOptions}
        divisionOptions={divisionOptions}
        handleCreateUser={userOperations.handleCreateUser}
        isCreatingUser={isCreatingUser}
      />

      <DepartmentModal
        showModal={showModalDepartment}
        setShowModal={setShowModalDepartment}
        resetForm={resetDepartmentForm}
        formData={departmentForm}
        setFormData={setDepartmentForm}
        formError={formError}
        handleSubmit={departmentOperations.handleCreateDepartment}
        isUpdating={false}
        hasChanges={false}
        title="Add Department"
        buttonText="Add Department"
      />

      <DepartmentModal
        showModal={showEditDepartmentModal}
        setShowModal={setShowEditDepartmentModal}
        resetForm={() => {
          setShowEditDepartmentModal(false);
          setOriginalDepartmentData({ name: "", description: "" });
        }}
        formData={editDepartmentData}
        setFormData={setEditDepartmentData}
        formError={formError}
        handleSubmit={departmentOperations.handleUpdateDepartment}
        isUpdating={isUpdatingDepartment}
        hasChanges={hasDepartmentChanges}
        title="Edit Department"
        buttonText="Update"
      />

      <DivisionModal
        showModal={showModalDivision}
        setShowModal={setShowModalDivision}
        resetForm={resetDivisionForm}
        divisionForm={divisionForm}
        setDivisionForm={setDivisionForm}
        selectedDepartment={selectedDepartment}
        setSelectedDepartment={setSelectedDepartment}
        departmentOptions={departmentOptions}
        formError={formError}
        handleSubmit={
          isEditingDivision
            ? divisionOperations.handleEditDivision
            : divisionOperations.handleCreateDivision
        }
        isUpdating={isUpdatingDivision}
        isEditing={isEditingDivision}
        title={isEditingDivision ? "Edit Division" : "Add Division"}
      />
    </div>
  );
};

export default CfmUser;
